import React, { useEffect, useState } from "react";
import Stack from "components/common/Stack";
import { Button, Popconfirm, Spin, Table, Typography } from "antd";
import { Link, RouteComponentProps } from "react-router-dom";
import { trackComponent } from "telemetry/AppInsights";
import sorters from "utils/sorters";
import {
  deleteRopeUpload,
  downloadRope,
  getRopeUploadsPageData
} from "../../api/ropeApi";
import {
  RopeUpload,
  RopeUploadsPageData,
  RopeUploadStatusDeletable
} from "../../types/Rope";
import displayErrorNotification from "../../utils/displayErrorNotification";
import DynamicsLogo from "../common/DynamicsLogo";
import ProjectorLogo from "../common/ProjectorLogo";
import { convertRopeFlagsToEntity } from "../../utils/convert";
import { formatDateTime } from "../../utils/formatDateTime";
import RopeMessagesModal from "./RopeMessagesModal";
import { createBrowserHistory } from "history";
import { textOverflow } from "../common/textOverflow";
import RopeUploadRecordModal from "./RopeUploadRecordModal";

const RopeUploadsPage = (props: RouteComponentProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RopeUploadsPageData>({
    rope: {
      id: 0,
      status: "",
      project_code: "",
      project_uid: "",
      project_name: "",
      client_name: "",
      office: "",
      start_date: new Date(),
      flags: "",
      entity: ""
    },
    uploads: []
  });
  // @ts-ignore
  const ropeId = props.match.params.ropeId;
  const history = createBrowserHistory({ basename: "" });
  const [selectedRecord, setSelectedRecord] = useState<RopeUpload | null>(null);
  const [isMessagesModelVisible, setIsMessagesModelVisible] = useState(false);
  const [maxUploadID, setMaxUploadID] = useState(-1);
  const [isRecordModalHidden, setIsRecordModalHidden] = useState(true);

  useEffect(() => {
    loadRopeUploadsPageData();
  }, []);

  const handleRecordOpenModal = record => {
    setIsRecordModalHidden(!isRecordModalHidden);
    setSelectedRecord(record);
  };

  function loadRopeUploadsPageData() {
    setLoading(true);
    getRopeUploadsPageData(ropeId)
      .then(response => {
        response.rope.entity = convertRopeFlagsToEntity(response.rope.flags);
        setMaxUploadID(
          response.uploads.length > 0
            ? Math.max(...response.uploads.map(u => u.id))
            : 0
        );
        setData(response);
      })
      .catch(displayErrorNotification)
      .finally(() => setLoading(false));
  }

  function openMessagesModal(record: RopeUpload) {
    setSelectedRecord(record);
    setIsMessagesModelVisible(true);
  }

  function handleDeleteRopeUpload(id: number) {
    if (id && id > 0) {
      setLoading(true);
      deleteRopeUpload(id)
        .then(() => {
          if (data.uploads.length === 1) {
            history.goBack();
          } else {
            loadRopeUploadsPageData();
          }
        })
        .catch(displayErrorNotification)
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function handleDownloadRope(id: number) {
    if (id && id > 0) {
      setLoading(true);
      downloadRope(id)
        .catch(displayErrorNotification)
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const RopeUploadsColumns = [
    {
      title: "Version",
      dataIndex: "version",
      onCell: r => {
        return {
          onClick: () => handleRecordOpenModal(r),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a, b) => sorters.number(a.version, b.version)
    },
    {
      title: "Status",
      dataIndex: "status",
      onCell: r => {
        return {
          onClick: () => handleRecordOpenModal(r),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a, b) => sorters.string(a.status, b.status)
    },
    {
      title: "Uploaded By",
      render: record => (
        <Link to={`/employees/${record.created_by_id}`}>
          {record.created_by}
        </Link>
      ),
      sorter: (a, b) => sorters.string(a.created_by, b.created_by)
    },
    {
      title: "Uploaded on",
      dataIndex: "created_at",
      render: data => formatDateTime(data),
      onCell: r => {
        return {
          onClick: () => handleRecordOpenModal(r),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a, b) => sorters.date(a.created_at, b.created_at)
    },
    {
      title: "Imported By",
      render: record =>
        record.updated_by_id && (
          <Link to={`/employees/${record.updated_by_id}`}>
            {record.updated_by}
          </Link>
        ),
      onCell: r => {
        if (!r.updated_by || r.updated_by === "") {
          return {
            onClick: () => handleRecordOpenModal(r),
            style: { cursor: "pointer" }
          };
        } else {
          return {};
        }
      },
      sorter: (a, b) => sorters.string(a.updated_by, b.updated_by)
    },
    {
      title: "Imported On",
      dataIndex: "updated_at",
      render: data => (data ? formatDateTime(data) : ""),
      onCell: r => {
        return {
          onClick: () => handleRecordOpenModal(r),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a, b) => sorters.date(a.created_at, b.created_at)
    },
    {
      title: "Comment",
      dataIndex: "comment",
      render: comment => textOverflow(comment, 40),
      onCell: r => {
        return {
          onClick: () => handleRecordOpenModal(r),
          style: { cursor: "pointer" }
        };
      },
      sorter: (a, b) => sorters.string(b.created_at, b.created_at)
    },
    {
      title: "Messages",
      render: record => (
        <Button
          onClick={() => openMessagesModal(record)}
          disabled={record.message_count === 0}
          size="small"
        >
          {record.message_count} Message
          {record.message_count !== 1 ? "s" : ""}
        </Button>
      )
    },
    {
      title: "Actions",
      render: record => (
        <>
          <Button
            icon={"download"}
            onClick={() => handleDownloadRope(record.id)}
            size="small"
          >
            Download
          </Button>
          &nbsp;
          <Popconfirm
            title={
              data.uploads.length === 1
                ? "Are you sure? This will also delete the ROPE record."
                : "Are you sure?"
            }
            onConfirm={() => handleDeleteRopeUpload(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={
              !RopeUploadStatusDeletable.includes(record.status) ||
              record.id < maxUploadID
            }
          >
            <Button
              icon={"delete"}
              size="small"
              disabled={
                !RopeUploadStatusDeletable.includes(record.status) ||
                record.id < maxUploadID
              }
            >
              Delete
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  return (
    <>
      {loading || data.rope.id === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh", // adjust as needed for vertical centering
            width: "100%"
          }}
        >
          <Spin size="large" style={{ margin: "20px" }} />
        </div>
      ) : (
        <Stack>
          <div
            style={{ display: "flex", alignItems: "center", height: "39px" }}
          >
            {data.rope.entity === "TA Digital" ? (
              <div style={{ marginTop: "16px" }}>
                <DynamicsLogo size="36px" />
              </div>
            ) : (
              <div style={{ marginTop: "-4px" }}>
                <ProjectorLogo size="22px" />
              </div>
            )}
            <Typography.Title level={1} style={{ marginBottom: 0 }}>
              {data.rope.project_name}
            </Typography.Title>
          </div>
          <Typography.Title
            level={3}
            type="secondary"
            style={{ marginTop: "0", marginBottom: "2rem" }}
          >
            {data.rope.client_name}
            {" · "}
            {data.rope.office}
            {" · "}
            {data.rope.start_date}
            {" · "}
            {data.rope.project_uid ? (
              <a
                href={`https://app.projectorpsa.com/x/ProjectLanding/${
                  data.rope.project_uid
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.rope.project_code}
              </a>
            ) : (
              data.rope.project_code
            )}
            {" · "}
            {data.rope.status}
          </Typography.Title>

          <Table
            style={{ width: "100%", clear: "both" }}
            pagination={false}
            rowKey={"id"}
            columns={RopeUploadsColumns}
            dataSource={data.uploads}
            loading={loading}
          />
          {isMessagesModelVisible && (
            <RopeMessagesModal
              modalState={isMessagesModelVisible}
              closeModal={() => setIsMessagesModelVisible(false)}
              record={selectedRecord || null}
            />
          )}
          {!isRecordModalHidden && (
            <RopeUploadRecordModal
              modalState={true}
              closeModal={() => setIsRecordModalHidden(true)}
              record={selectedRecord || null}
            />
          )}
        </Stack>
      )}
    </>
  );
};

export default trackComponent(RopeUploadsPage, "ROPE Detail Page");
